/**
 * Demo KPI fixtures. A realistic split across three operational
 * dimensions — execution health, throughput, and customer impact —
 * that map naturally to the demo scenarios above. The dashboard +
 * value pages all share these ids so the cross-page navigation
 * hangs together.
 *
 * **Shape note**: id fields are numbers (matching live `kpi_type.id` /
 * `kpi_metric.id`). Pre-expansion fixtures used string ids (`'demo-type-…'`)
 * which fell through `asKpiType` / `asKpiMetric` type-narrowing and
 * silently rendered as `id: 0` — invisible bug in the demo path.
 * Operator-reported: "no KPI values".
 *
 * Kpi keys now use the live `{ fusionScenarioId, metrics: number[] }`
 * shape consumed by `ValueScenarioKpisPage` — previously
 * `{ scenarioId, metricId }` which didn't match anything in the SPA.
 */

type Row = Record<string, unknown>;

export const DEMO_KPI_TYPES: ReadonlyArray<Row> = [
  { id: 101, name: 'Execution health', color: '#ef4444', icon: 'AlertTriangle' },
  { id: 102, name: 'Throughput', color: '#3b82f6', icon: 'TrendingUp' },
  { id: 103, name: 'Customer impact', color: '#22c55e', icon: 'Target' },
  { id: 104, name: 'Cost', color: '#f59e0b', icon: 'DollarSign' },
  { id: 105, name: 'Compliance', color: '#a855f7', icon: 'ShieldCheck' },
];

export const DEMO_KPI_METRICS: ReadonlyArray<Row> = [
  // Execution health
  {
    id: 201,
    displayName: 'Error rate',
    internalName: 'error_rate',
    color: '#ef4444',
    icon: 'AlertTriangle',
    kpiTypeId: 101,
  },
  {
    id: 202,
    displayName: 'Successful runs',
    internalName: 'runs_success',
    color: '#22c55e',
    icon: 'CheckCircle2',
    kpiTypeId: 101,
  },
  {
    id: 203,
    displayName: 'Warning rate',
    internalName: 'warning_rate',
    color: '#f59e0b',
    icon: 'AlertCircle',
    kpiTypeId: 101,
  },
  // Throughput
  {
    id: 204,
    displayName: 'Operations / day',
    internalName: 'operations_per_day',
    color: '#3b82f6',
    icon: 'Activity',
    kpiTypeId: 102,
  },
  {
    id: 205,
    displayName: 'Records transferred',
    internalName: 'records_transferred',
    color: '#0ea5e9',
    icon: 'ArrowRightLeft',
    kpiTypeId: 102,
  },
  {
    id: 206,
    displayName: 'Avg duration (s)',
    internalName: 'avg_duration_s',
    color: '#06b6d4',
    icon: 'Clock',
    kpiTypeId: 102,
  },
  // Customer impact
  {
    id: 207,
    displayName: 'Workfront issues created',
    internalName: 'wf_issues_created',
    color: '#22c55e',
    icon: 'Plus',
    kpiTypeId: 103,
  },
  {
    id: 208,
    displayName: 'Estimated hours saved / month',
    internalName: 'hours_saved_monthly',
    color: '#f59e0b',
    icon: 'Clock',
    kpiTypeId: 103,
  },
  {
    id: 209,
    displayName: 'NPS responses processed',
    internalName: 'nps_processed',
    color: '#10b981',
    icon: 'MessageSquare',
    kpiTypeId: 103,
  },
  // Cost
  {
    id: 210,
    displayName: 'Cost / 1k ops',
    internalName: 'cost_per_kop',
    color: '#f59e0b',
    icon: 'DollarSign',
    kpiTypeId: 104,
  },
  // Compliance
  {
    id: 211,
    displayName: 'Backup success',
    internalName: 'backup_success',
    color: '#a855f7',
    icon: 'ShieldCheck',
    kpiTypeId: 105,
  },
];

/**
 * `kpi_key` rows link scenarios → attached metrics. Live shape:
 * `{ fusionScenarioId: string, metrics: number[] }` where the metrics
 * array is the deduped set of attached metric ids. One row per
 * scenario; the `ValueScenarioKpisPage` cross-table flattens these
 * out into one row per (scenario, metric) for the chip grid.
 *
 * Coverage — every active scenario gets execution + throughput
 * metrics; a curated subset gets impact / cost / compliance to add
 * variety to the chip grid. Inactive scenarios are deliberately
 * uncovered (legacy parity: KPIs only attach to active scenarios).
 */
export const DEMO_KPI_KEYS: ReadonlyArray<Row> = [
  // Operations heavyweights
  { fusionScenarioId: '101001', metrics: [201, 202, 204, 207, 208] },
  { fusionScenarioId: '101002', metrics: [201, 204, 209] },
  { fusionScenarioId: '101003', metrics: [201, 202, 204, 207] },
  { fusionScenarioId: '101004', metrics: [201, 205, 209] },
  { fusionScenarioId: '101005', metrics: [201, 208] },
  { fusionScenarioId: '101006', metrics: [201, 204] },
  // 101007 + 101008 inactive — no KPIs (parity)
  { fusionScenarioId: '101009', metrics: [201, 204, 208] },
  { fusionScenarioId: '101010', metrics: [205, 206] },
  { fusionScenarioId: '101011', metrics: [201, 207, 209] },
  { fusionScenarioId: '101012', metrics: [201, 204, 205] },
  { fusionScenarioId: '101013', metrics: [201, 208] },
  { fusionScenarioId: '101014', metrics: [201, 204, 210] },
  { fusionScenarioId: '101015', metrics: [208] },
  // AI automations
  { fusionScenarioId: '101016', metrics: [201, 204, 210] },
  { fusionScenarioId: '101017', metrics: [201, 208] },
  { fusionScenarioId: '101018', metrics: [201, 204, 210] },
  { fusionScenarioId: '101019', metrics: [201, 208, 210] },
  { fusionScenarioId: '101020', metrics: [208] },
  // Maintenance + compliance
  { fusionScenarioId: '101021', metrics: [201, 211] },
  { fusionScenarioId: '101022', metrics: [201, 211] },
  // Customer-facing
  { fusionScenarioId: '101023', metrics: [201, 204, 209] },
  { fusionScenarioId: '101024', metrics: [201, 208, 209] },
  { fusionScenarioId: '101025', metrics: [201, 204, 210] },
  // 101026 inactive — no KPIs
  { fusionScenarioId: '101027', metrics: [201, 208] },
  { fusionScenarioId: '101028', metrics: [205, 206, 210] },
  { fusionScenarioId: '101029', metrics: [201, 204] },
  // 101030 inactive — no KPIs
];

/**
 * Demo KPI values (per-scenario, per-metric, daily aggregates).
 * Shape matches the engine's `/v1/kpi-values` response when that
 * hook lands. Until then, this fixture seeds the value-page graphs
 * with realistic-looking time series.
 *
 * The numbers below are deterministic (seeded from scenarioId +
 * metricId) so the same demo always renders the same chart values —
 * we don't want the demo's screenshot to change every page reload.
 */
export interface DemoKpiValue {
  fusionScenarioId: string;
  metricId: number;
  day: string; // YYYY-MM-DD
  value: number;
}

function seededValue(
  scenarioId: string,
  metricId: number,
  dayOffset: number,
  base: number,
  variance: number,
): number {
  // Tiny PRNG seeded from inputs — deterministic, no Math.random().
  const seed = (parseInt(scenarioId, 10) * 31 + metricId * 17 + dayOffset * 13) % 997;
  return Math.max(0, Math.round(base + (seed / 997 - 0.5) * 2 * variance));
}

function generateDailyValues(
  fusionScenarioId: string,
  metricId: number,
  baseValue: number,
  variance: number,
  days = 30,
): DemoKpiValue[] {
  const today = new Date();
  return Array.from({ length: days }, (_, i) => {
    const d = new Date(today);
    d.setUTCDate(d.getUTCDate() - (days - 1 - i));
    const day = d.toISOString().slice(0, 10);
    return {
      fusionScenarioId,
      metricId,
      day,
      value: seededValue(fusionScenarioId, metricId, i, baseValue, variance),
    };
  });
}

// Per-metric "base" levels — gives each metric a believable scale.
const METRIC_BASES: Record<number, { base: number; variance: number }> = {
  201: { base: 2, variance: 1 }, // error rate (low single digits)
  202: { base: 120, variance: 30 }, // successful runs
  203: { base: 8, variance: 3 },
  204: { base: 2400, variance: 600 }, // operations/day
  205: { base: 850, variance: 200 },
  206: { base: 45, variance: 15 },
  207: { base: 16, variance: 6 }, // wf issues created
  208: { base: 24, variance: 8 }, // hours saved
  209: { base: 12, variance: 4 },
  210: { base: 1.2, variance: 0.3 }, // cost / 1k ops
  211: { base: 99, variance: 1 }, // backup success rate %
};

// Build the values matrix from KPI keys, one daily series per
// (scenario, metric) tuple. The output is a flat list — consumer
// code groups as needed.
export const DEMO_KPI_VALUES: DemoKpiValue[] = DEMO_KPI_KEYS.flatMap((k) => {
  const fusionScenarioId = (k.fusionScenarioId as string) ?? '';
  const metrics = (k.metrics as number[]) ?? [];
  return metrics.flatMap((metricId) => {
    const { base, variance } = METRIC_BASES[metricId] ?? { base: 100, variance: 20 };
    return generateDailyValues(fusionScenarioId, metricId, base, variance);
  });
});

/**
 * Lookup helper — returns the daily series for one (scenario, metric)
 * pair. Empty array if the pair isn't covered.
 */
export function getDemoKpiValues(fusionScenarioId: string, metricId: number): DemoKpiValue[] {
  return DEMO_KPI_VALUES.filter(
    (v) => v.fusionScenarioId === fusionScenarioId && v.metricId === metricId,
  );
}

/**
 * Same data as `DEMO_KPI_VALUES`, but reshaped to match the engine's
 * `GET /v1/kpi/values` wire shape — one row per
 * `(fusionScenarioId, datePart)` tuple with a `metrics: Record<metricId,
 * {value, quantity}>` map. Drives the `useKpiValues` hook in demo
 * mode so the Dashboard / Value / Presentation-detail surfaces have
 * realistic-looking numbers without an engine round-trip.
 *
 * The per-metric `quantity` mirrors the operational interpretation
 * the legacy uses: `value` = monetary, `quantity` = hours / count of
 * events. We split the seeded daily value here so demo cells render
 * sensible $-and-hour pairs.
 */
interface DemoKpiValueItemMetrics {
  value: number;
  quantity: number;
  count: number;
}
interface DemoKpiValueItem {
  fusionScenarioId: string;
  datePart: string;
  metrics: Record<string, DemoKpiValueItemMetrics>;
}

export const DEMO_KPI_VALUES_ITEMS: ReadonlyArray<DemoKpiValueItem> = (() => {
  const map = new Map<string, DemoKpiValueItem>();
  for (const v of DEMO_KPI_VALUES) {
    const key = `${v.fusionScenarioId}|${v.day}`;
    let item = map.get(key);
    if (!item) {
      item = { fusionScenarioId: v.fusionScenarioId, datePart: v.day, metrics: {} };
      map.set(key, item);
    }
    item.metrics[String(v.metricId)] = {
      // Split the seeded value into a dollar amount + an hours number.
      // Both come off the same seed so the demo stays deterministic.
      value: v.value * 12,
      quantity: Math.round(v.value / 4),
      count: v.value,
    };
  }
  return [...map.values()];
})();
